import { animate, state, style, transition, trigger } from '@angular/animations';
import { DataSource, SelectionModel } from '@angular/cdk/collections';
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { Router } from '@angular/router';
import { PaymentAccount, ReservationChargeDetail } from '@uzurv/clara';
import { TimezoneDateProvider } from '@uzurv/core';
import { Observable, of } from 'rxjs';

@Component({
  selector: 'app-collect-payment',
  templateUrl: './collect-payment.component.html',
  styleUrls: ['./collect-payment.component.scss'],
  animations: [
    trigger('detailExpand', [
      state('collapsed', style({ height: '0px', minHeight: '0' })),
      state('expanded', style({ height: '*' })),
      transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)'))
    ]),
    trigger('indicatorRotate', [
      state('closed', style({ transform: 'rotate(0deg)' })),
      state('open', style({ transform: 'rotate(180deg)' })),
      transition('open <=> closed', animate('225ms cubic-bezier(0.4,0.0,0.2,1)'))
    ])
  ]
})
export class CollectPaymentComponent implements OnChanges {
  @Input() paymentAccounts: Array<PaymentAccount>;
  @Input() outstandingCharges: Array<ReservationChargeDetail>;

  @Output() editPaymentAccount: EventEmitter<PaymentAccount> = new EventEmitter();
  @Output() submitPayment: EventEmitter<{
    selectedPaymentAccount: PaymentAccount;
    selectedCharges: Array<ReservationChargeDetail>;
  }> = new EventEmitter();

  panelOpenState = false;
  chargeDataSource: DataSource<ReservationChargeDetail>;
  displayedChargedColumns;
  expandedReservationCharge: ReservationChargeDetail | null;
  selection = new SelectionModel<ReservationChargeDetail>(true, []);
  selectedPaymentAccount: PaymentAccount = null;

  chargesColumns = [
    { columnDef: 'select', title: 'Select', cell: () => '' },
    { columnDef: 'amount', title: 'Amount', cell: (reservationCharge: ReservationChargeDetail) => `${reservationCharge.amount}` },
    { columnDef: 'date', title: 'Date', cell: (reservationCharge: ReservationChargeDetail) => `${reservationCharge.createdDate}` },
    {
      columnDef: 'status',
      title: 'status',
      cell: (reservationCharge: ReservationChargeDetail) => `${reservationCharge.payment.paymentStatus.name}`
    },
    {
      columnDef: 'description',
      title: 'description',
      cell: (reservationCharge: ReservationChargeDetail) => `${reservationCharge.description}`
    },
    {
      columnDef: 'reservation',
      title: 'reservation',
      cell: (reservationCharge: ReservationChargeDetail) => `${reservationCharge.reservationDetail.id}`
    }
  ];

  constructor(public timeZoneDateProvider: TimezoneDateProvider, private router: Router) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['outstandingCharges'] && changes['outstandingCharges'].currentValue) {
      this.displayedChargedColumns = this.chargesColumns.map(c => c.columnDef);
      this.chargeDataSource = new ReservationChargeDetailDataSource(this.outstandingCharges);
      this.selection.clear();
      this.outstandingCharges.forEach(row => this.selection.select(row));
    }
  }

  isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this.outstandingCharges.length;
    return numSelected === numRows;
  }

  /** Selects all rows if they are not all selected; otherwise clear selection. */
  masterToggle() {
    this.isAllSelected() ? this.selection.clear() : this.outstandingCharges.forEach(row => this.selection.select(row));
  }

  handleEditPaymentAccount(paymentAccount: PaymentAccount): void {
    this.editPaymentAccount.emit(paymentAccount);
  }

  handleExpandMore(reservationCharge: ReservationChargeDetail) {
    //  doing it this way since we can't rely on the ReservationChargeDetail to have the full paymentAccount information. (Phil approved)
    this.paymentAccounts.forEach((paymentAccount: PaymentAccount) => {
      if (
        reservationCharge.payment.paymentAccount.cardNumber === paymentAccount.cardNumber &&
        reservationCharge.payment.paymentAccount.cardExpiration === paymentAccount.cardExpiration
      ) {
        reservationCharge.payment.paymentAccount = paymentAccount;
        return;
      }
    });

    this.expandedReservationCharge = this.expandedReservationCharge === reservationCharge ? null : reservationCharge;
  }
  handleGoToReservation(reservationId: number): void {
    this.router.navigateByUrl(`/rides/res-${reservationId}`);
  }

  getTotal(): number {
    const selectedOrderIds = [];
    let totalAmount = 0;
    this.selection.selected.forEach(charge => {
      selectedOrderIds.push(charge.id);
      totalAmount += charge.amount;
    });
    return totalAmount;
  }

  submit(): void {
    this.submitPayment.emit({
      selectedPaymentAccount: this.selectedPaymentAccount,
      selectedCharges: this.selection.selected
    });
  }
}

export class ReservationChargeDetailDataSource extends DataSource<ReservationChargeDetail> {
  private dataBase: any;
  constructor(data: Array<ReservationChargeDetail>) {
    super();
    this.dataBase = data;
  }

  /** Connect function called by the table to retrieve one stream containing the data to render. */
  connect(): Observable<Array<ReservationChargeDetail>> {
    const rows = [];
    this.dataBase.forEach(reservationChargeDetail => rows.push(reservationChargeDetail));
    return of(rows);
  }

  disconnect() {}
}
